import { Link } from 'react-router-dom'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

function DocumentationPage() {
  usePageMeta({
    title: 'Rithmo Documentation: Getting Started',
    description: 'Step-by-step guides and video walkthroughs to set up and use Rithmo. Learn how to add Rithmo to Zoom meetings, configure agendas, and review outputs.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-24">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-50/60 via-white to-white" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              Documentation
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Watch the product walkthrough and learn how Rithmo brings meeting governance to life.
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-8 text-left">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Help: Security &amp; privacy</h2>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                For IT, security, privacy, and procurement review, see the Security &amp; AI Privacy Whitepaper
                (architecture, data flows, AI egress model, and subprocessors summary).
              </p>
              <Link
                to="/security-ai-privacy-whitepaper"
                className="inline-flex text-primary-600 font-medium hover:underline"
              >
                Open Security &amp; AI Privacy Whitepaper
              </Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="aspect-video">
                <iframe
                  src="https://player.mux.com/B9utUHzgX8EVPU5RS01PxzwbqtK2ubguTAlelg6ubRvE"
                  style={{ width: '100%', border: 'none', aspectRatio: '16/9' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  title="Rithmo Documentation"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="prose prose-gray max-w-none text-gray-700 space-y-8">
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Overview</h2>
                  <p className="text-gray-600 leading-relaxed">
                    Rithmo is an AI meeting host for Zoom. Users connect their Zoom account to
                    Rithmo, open the Rithmo Zoom App during a live meeting, and use Rithmo for
                    in-meeting support and post-meeting insights.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Adding the App</h2>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Prerequisites</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Before using Rithmo with Zoom, the user must have:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                    <li>a Rithmo account</li>
                    <li>a Zoom account</li>
                    <li>access to the Zoom meeting they want to use with Rithmo</li>
                    <li>permission to authorize Zoom for their account</li>
                  </ul>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">How to Add and Authorize the App</h3>
                  <ol className="list-decimal pl-5 mt-2 space-y-2 text-gray-600">
                    <li>
                      Go to{' '}
                      <a
                        href="https://rithmo.ai"
                        rel="noopener noreferrer"
                        target="_blank"
                        className="text-primary-600 hover:underline"
                      >
                        https://rithmo.ai
                      </a>
                    </li>
                    <li>Sign in to your Rithmo account</li>
                    <li>
                      From the Rithmo product, connect your Zoom account through the Zoom OAuth
                      authorization flow
                    </li>
                    <li>Review the requested Zoom permissions</li>
                    <li>Approve the authorization request</li>
                    <li>Return to Rithmo and confirm that your Zoom account is connected</li>
                  </ol>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Opening Rithmo in a Zoom Meeting</h3>
                  <ol className="list-decimal pl-5 mt-2 space-y-2 text-gray-600">
                    <li>Start or join a Zoom meeting</li>
                    <li>Open Apps inside the Zoom meeting client</li>
                    <li>Search for Rithmo</li>
                    <li>Open the Rithmo Zoom App</li>
                    <li>Use the in-meeting Rithmo experience during the live meeting</li>
                  </ol>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Troubleshooting</h3>
                  <p className="text-gray-600 leading-relaxed">
                    If you have trouble adding or authorizing the app:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                    <li>make sure you are signed into the correct Zoom account</li>
                    <li>make sure pop-ups are enabled in your browser</li>
                    <li>reconnect Zoom from the Rithmo account flow if needed</li>
                    <li>make sure you are using a supported Zoom meeting client</li>
                    <li>
                      contact support at{' '}
                      <a
                        href="mailto:support@rithmo.ai"
                        className="text-primary-600 font-medium hover:underline"
                      >
                        support@rithmo.ai
                      </a>
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Usage</h2>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">What Rithmo Does</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Rithmo provides an in-meeting Zoom App experience and related meeting workflow
                    support.
                  </p>
                  <p className="text-gray-600 mt-3 leading-relaxed">
                    Depending on configuration and the meeting context, users may be exposed to:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                    <li>meeting context awareness inside the Zoom meeting client</li>
                    <li>participant-aware meeting support</li>
                    <li>in-meeting notifications</li>
                    <li>in-meeting messaging and chat-related interactions</li>
                    <li>meeting guidance and structured meeting support</li>
                    <li>post-meeting outputs and insights in the Rithmo experience</li>
                  </ul>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Prerequisites for Use</h3>
                  <p className="text-gray-600 leading-relaxed">To use Rithmo in Zoom, the user must:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                    <li>be signed into Rithmo</li>
                    <li>have connected their Zoom account</li>
                    <li>be in a supported Zoom meeting</li>
                    <li>open the Rithmo app from the Zoom meeting client</li>
                  </ul>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Zoom Meeting Surface</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Rithmo uses the Meeting surface because users open and interact with the app
                    inside the Zoom meeting client during a live meeting.
                  </p>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Post-Meeting Use</h3>
                  <p className="text-gray-600 leading-relaxed">
                    After a supported meeting, users can access generated outputs and meeting
                    insights in Rithmo.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Removing the App</h2>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">
                    How to Remove or Disconnect Rithmo
                  </h3>
                  <ol className="list-decimal pl-5 mt-2 space-y-2 text-gray-600">
                    <li>Sign in to your Rithmo account</li>
                    <li>Go to the Zoom connection or integration area in the product</li>
                    <li>Disconnect your Zoom account</li>
                    <li>
                      If required, also remove or de-authorize the app from your Zoom account settings
                    </li>
                  </ol>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">
                    What Happens After De-Authorization
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    After de-authorization or disconnecting Zoom:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                    <li>Rithmo can no longer access Zoom data for future use</li>
                    <li>Zoom-connected functionality stops working until the app is authorized again</li>
                    <li>
                      users may lose access to Zoom-dependent workflows until reconnection is
                      completed
                    </li>
                  </ul>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Data Handling After Removal</h3>
                  <p className="text-gray-600 leading-relaxed">
                    After a user disconnects or de-authorizes Zoom, Rithmo stops future access to
                    that Zoom account through the integration. Previously created records are
                    handled according to the Rithmo privacy policy and retention process. Users can
                    request deletion of eligible stored data by contacting{' '}
                    <a
                      href="mailto:privacy@rithmo.ai"
                      className="text-primary-600 font-medium hover:underline"
                    >
                      privacy@rithmo.ai
                    </a>
                    .
                  </p>

                  <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Additional Notes</h3>
                  <p className="text-gray-600 leading-relaxed">
                    If the user reconnects Zoom later, they must complete authorization again before
                    using Zoom-dependent features.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default DocumentationPage
